import { useState, useEffect } from "react";
import {
  Box,
  FormControlLabel,
  Checkbox,
  Typography,
  Stack,
} from "@mui/material";
import { FormHelperTextError } from "../form/errors";
import { EmailAddressBox } from "../form/EmailAddressBox";
import { AppButton, DefaultButton, PrimaryButton } from "../form";
import ModalBox from "./index";

interface AddMembersModalProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (data: { emails: string[]; soloAttendance: boolean }) => void;
  initialEmails?: string[];
  initialSoloAttendance?: boolean;
  title?: string;
  submitButtonText?: string;
  cancelButtonText?: string;
}

export function AddMembersModal({
  open,
  onClose,
  onSubmit,
  initialEmails = [],
  initialSoloAttendance = false,
  title = "Add Attendees",
  submitButtonText = "Submit",
  cancelButtonText = "Cancel",
}: AddMembersModalProps) {
  const [emailIds, setEmailIds] = useState<{
    list: string[];
    invalidEmailIds: string[];
  }>({
    list: initialEmails,
    invalidEmailIds: [],
  });
  const [soloAttendanceChecked, setSoloAttendanceChecked] = useState(
    initialSoloAttendance
  );
  const [showError, setShowError] = useState(false);

  useEffect(() => {
    if (open) {
      setEmailIds({
        list: initialEmails,
        invalidEmailIds: [],
      });
      setSoloAttendanceChecked(initialSoloAttendance);
      setShowError(false);
    }
  }, [open, initialEmails, initialSoloAttendance]);

  const handleOnEmailAddressesChange = (payload: {
    list: string[];
    invalidEmailIds: string[];
  }) => {
    setEmailIds(payload);
    setShowError(false);
    
    if (payload.list.length > 0) {
      setSoloAttendanceChecked(false);
    }
  };

  const handleSoloAttendanceChange = (checked: boolean) => {
    setSoloAttendanceChecked(checked);
    setShowError(false);
    
    if (checked) {
      setEmailIds({ list: [], invalidEmailIds: [] });
    }
  };

  const validateAndSubmit = () => {
    
    const hasValidEmails =
      emailIds.list.length > 0 && emailIds.invalidEmailIds.length === 0;
    const hasSoloAttendance = soloAttendanceChecked;

    if (!hasValidEmails && !hasSoloAttendance) {
      setShowError(true);
      return;
    }

    if (emailIds.invalidEmailIds.length > 0) {
      setShowError(true);
      return;
    }

    onSubmit({
      emails: emailIds.list,
      soloAttendance: soloAttendanceChecked,
    });
  };

  return (
    <ModalBox
      open={open}
      onClose={onClose}
      title={title}
      showFooter={false}
      maxWidth="sm"
    >
      <Stack gap={1}>
        <Typography variant="body2" sx={{ color: "text.secondary" }}>
          Please add the email addresses of all attendees who will be joining
          the call. If you will be attending alone, please check the checkbox below.
        </Typography>

        <EmailAddressBox
          inlineLabel
          onValueChange={handleOnEmailAddressesChange}
          initialValue={emailIds.list}
        />

        <Box>
          <FormControlLabel
            control={
              <Checkbox
                checked={soloAttendanceChecked}
                onChange={(e) => handleSoloAttendanceChange(e.target.checked)}
              />
            }
            label={
              <Typography variant="body2">
                No additional representatives will join — I will attend the
                meeting myself.
              </Typography>
            }
          />
        </Box>

        <Box>
          {showError &&
            emailIds.invalidEmailIds.length === 0 &&
            !soloAttendanceChecked &&
            emailIds.list.length === 0 && (
              <Typography variant="caption" color="error">
                Please add attendee email addresses or confirm that no
                additional representatives will join
              </Typography>
            )}
        </Box>
        <Stack direction="row" mt={2} spacing={2} justifyContent="flex-end">
          <DefaultButton
            onClick={onClose}
          >
            {cancelButtonText}
          </DefaultButton>
          <PrimaryButton
            onClick={validateAndSubmit}
          >
            {submitButtonText}
          </PrimaryButton>
        </Stack>
      </Stack>
    </ModalBox>
  );
}
